import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverHeader,
  Checkbox,
  CheckboxGroup,
  HStack,
  Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/footer";
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaFilter,
} from "react-icons/fa";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const API_PRECOS = "/api/frota/historico-precos";
const API_FORNECEDORES = "/api/frota/fornecedores";
const API_PRODUTOS = "/api/frota/produtos";
const ITEMS_PER_PAGE = 25;

const CORES_GRAFICO = [
  "#007abe",
  "#22c55e",
  "#eab308",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#f97316",
];

/** Formata data ISO (YYYY-MM-DD) para DD-MM-AAAA */
function formatarData(str) {
  if (!str) return "–";
  const part = String(str).split(/[-T]/);
  if (part.length < 3) return str;
  const [y, m, d] = part;
  return `${d}-${m}-${y}`;
}

function formatPrecoValor(v) {
  return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 4 });
}

function formatPrecoEixo(v) {
  if (typeof v !== "number") return "";
  return `R$ ${v.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;
}

function calcDominioPreco(values) {
  const nums = values.filter((v) => v != null && !Number.isNaN(Number(v)));
  if (nums.length === 0) return [0, 10];
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const pad = (max - min) * 0.12 || max * 0.05 || 0.5;
  return [Math.max(0, min - pad), max + pad];
}

/** Modal isolado: estado local evita re-render da página ao digitar */
const ModalRegistrarPrecos = React.memo(function ModalRegistrarPrecos({
  isOpen,
  onClose,
  fornecedores,
  produtos,
  onSuccess,
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [fornecedorId, setFornecedorId] = useState("");
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [prazoPagamento, setPrazoPagamento] = useState(0);
  const [openId, setOpenId] = useState(0);
  const precosRef = React.useRef({});

  useEffect(() => {
    if (isOpen && produtos.length > 0) {
      setData(new Date().toISOString().slice(0, 10));
      setFornecedorId("");
      setPrazoPagamento(0);
      setOpenId((i) => i + 1);
      precosRef.current = {};
    }
  }, [isOpen, produtos]);

  const reportPreco = useCallback((id, v) => {
    precosRef.current[id] = v;
  }, []);

  const handleSave = async () => {
    if (!fornecedorId) {
      toast({
        title: "Selecione o fornecedor",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    const precos = Object.entries(precosRef.current)
      .filter(([, v]) => v !== "" && v != null && !Number.isNaN(parseFloat(v)))
      .map(([produto_id, valor]) => ({ produto_id, valor: parseFloat(valor) }));
    if (precos.length === 0) {
      toast({
        title: "Informe ao menos um preço para algum produto",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(API_PRECOS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fornecedor_id: fornecedorId,
          data,
          prazo_pagamento: Number(prazoPagamento) || 0,
          precos,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Erro ao salvar");
      toast({
        title: result.count
          ? `${result.count} preço(s) registrado(s) com sucesso`
          : "Preço registrado com sucesso",
        status: "success",
        isClosable: true,
      });
      onClose();
      onSuccess();
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Registrar preços do dia</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" pb={4}>
          <Flex direction="column" gap={4}>
            <FormControl isRequired>
              <FormLabel>Fornecedor</FormLabel>
              <Select
                placeholder="Selecione o fornecedor"
                value={fornecedorId}
                onChange={(e) => setFornecedorId(e.target.value)}
              >
                {fornecedores.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </Select>
            </FormControl>
            <Flex gap={4} flexWrap="wrap">
              <FormControl isRequired flex="1" minW="140px">
                <FormLabel>Data</FormLabel>
                <Input
                  type="date"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              </FormControl>
              <FormControl maxW="120px">
                <FormLabel>Prazo (dias)</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={prazoPagamento}
                  onChange={(e) => setPrazoPagamento(e.target.value)}
                />
              </FormControl>
            </Flex>
            <Box>
              <Text fontWeight="semibold" mb={3} color="gray.700">
                Preço do dia por produto (preencha os que deseja registrar)
              </Text>
              {produtos.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  Nenhum produto cadastrado. Cadastre produtos em Configuração.
                </Text>
              ) : (
                <Flex direction="column" gap={2}>
                  {produtos.map((p) => (
                    <PrecoProdutoRow
                      key={`${p.id}-${openId}`}
                      produtoId={p.id}
                      produtoNome={p.nome}
                      onReport={reportPreco}
                    />
                  ))}
                </Flex>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            bg="#007abe"
            color="white"
            onClick={handleSave}
            isLoading={saving}
            _hover={{ bg: "#0066a3" }}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

const ModalEditarPreco = React.memo(function ModalEditarPreco({
  isOpen,
  onClose,
  row,
  onSuccess,
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState("");
  const [valor, setValor] = useState("");
  const [prazoPagamento, setPrazoPagamento] = useState(0);
  const [observacoes, setObservacoes] = useState("");

  useEffect(() => {
    if (isOpen && row) {
      const dataStr = row.data ? String(row.data).slice(0, 10) : "";
      setData(dataStr);
      setValor(row.valor != null ? String(row.valor) : "");
      setPrazoPagamento(row.prazo_pagamento ?? 0);
      setObservacoes(row.observacoes ?? "");
    }
  }, [isOpen, row]);

  const nomeFornecedor =
    row?.fornecedores?.nome ?? row?.fornecedor_id ?? "–";
  const nomeProduto = row?.produtos?.nome ?? row?.produto_id ?? "–";

  const handleSave = async () => {
    if (!row?.id) return;
    if (valor === "" || Number.isNaN(parseFloat(valor))) {
      toast({
        title: "Informe um valor válido",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    if (!data) {
      toast({
        title: "Informe a data",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_PRECOS}?id=${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: row.id,
          data,
          valor: parseFloat(valor),
          prazo_pagamento: Number(prazoPagamento) || 0,
          observacoes: observacoes.trim() || null,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Erro ao salvar");
      toast({
        title: "Preço atualizado com sucesso",
        status: "success",
        isClosable: true,
      });
      onClose();
      onSuccess();
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Editar preço</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <Flex direction="column" gap={4}>
            <FormControl>
              <FormLabel>Fornecedor</FormLabel>
              <Input value={nomeFornecedor} isReadOnly bg="gray.50" />
            </FormControl>
            <FormControl>
              <FormLabel>Produto</FormLabel>
              <Input value={nomeProduto} isReadOnly bg="gray.50" />
            </FormControl>
            <Flex gap={4} flexWrap="wrap">
              <FormControl isRequired flex="1" minW="140px">
                <FormLabel>Data</FormLabel>
                <Input
                  type="date"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired flex="1" minW="140px">
                <FormLabel>Valor (R$)</FormLabel>
                <Input
                  type="number"
                  step="0.0001"
                  min={0}
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                />
              </FormControl>
              <FormControl maxW="120px">
                <FormLabel>Prazo (dias)</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={prazoPagamento}
                  onChange={(e) => setPrazoPagamento(e.target.value)}
                />
              </FormControl>
            </Flex>
            <FormControl>
              <FormLabel>Observações</FormLabel>
              <Input
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Opcional"
              />
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            bg="#007abe"
            color="white"
            onClick={handleSave}
            isLoading={saving}
            _hover={{ bg: "#0066a3" }}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

/** Linha do produto: estado local para não re-renderar outras ao digitar */
const PrecoProdutoRow = React.memo(function PrecoProdutoRow({
  produtoId,
  produtoNome,
  onReport,
}) {
  const [value, setValue] = useState("");
  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    onReport(produtoId, v);
  };
  return (
    <Flex align="center" gap={3} p={2} bg="gray.50" borderRadius="md">
      <Text fontWeight="medium" minW="140px" fontSize="sm">
        {produtoNome}
      </Text>
      <Input
        type="number"
        step="0.0001"
        placeholder="R$"
        value={value}
        onChange={handleChange}
        maxW="160px"
        size="sm"
      />
      <Text fontSize="xs" color="gray.500">
        R$/un.
      </Text>
    </Flex>
  );
});

const HistoricoPrecosPage = () => {
  const [lista, setLista] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroDe, setFiltroDe] = useState("");
  const [filtroAte, setFiltroAte] = useState("");
  const [produtoTabIndex, setProdutoTabIndex] = useState(0);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [columnFilters, setColumnFilters] = useState({
    fornecedor: [],
    produto: [],
    data: [],
    valor: [],
    prazo_pagamento: [],
  });
  const [sortAnaliseMelhor, setSortAnaliseMelhor] = useState(null);
  const [sortDirAnaliseMelhor, setSortDirAnaliseMelhor] = useState("asc");
  const [filtersAnaliseMelhor, setFiltersAnaliseMelhor] = useState({
    fornecedor: [],
    produto: [],
    menorPreco: [],
    prazoPag: [],
    variacao: [],
    precoMedio: [],
  });
  const [sortAnaliseAlt, setSortAnaliseAlt] = useState(null);
  const [sortDirAnaliseAlt, setSortDirAnaliseAlt] = useState("asc");
  const [filtersAnaliseAlt, setFiltersAnaliseAlt] = useState({
    fornecedor: [],
    produto: [],
    menorPreco: [],
    prazoPag: [],
    variacao: [],
    precoMedio: [],
  });
  const [selectedProductKeys, setSelectedProductKeys] = useState(
    () => new Set()
  );
  const [selectedFornecedorKeys, setSelectedFornecedorKeys] = useState(
    () => new Set()
  );
  const [tablePage, setTablePage] = useState(1);
  const toggleProductKey = useCallback((key) => {
    setSelectedProductKeys((prev) => {
      const next = new Set(prev);
      if (next.size === 0) {
        return new Set([key]);
      }
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const toggleFornecedorKey = useCallback((key) => {
    setSelectedFornecedorKeys((prev) => {
      const next = new Set(prev);
      if (next.size === 0) {
        return new Set([key]);
      }
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const [editingRow, setEditingRow] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();

  const loadPrecos = async () => {
    setLoading(true);
    try {
      let url = API_PRECOS;
      const params = new URLSearchParams();
      if (filtroDe) params.set("de", filtroDe);
      if (filtroAte) params.set("ate", filtroAte);
      if (params.toString()) url += `?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Erro ao carregar preços");
      const data = await res.json();
      setLista(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
      setLista([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFornecedores = async () => {
    try {
      const res = await fetch(API_FORNECEDORES);
      if (res.ok) {
        const data = await res.json();
        setFornecedores(Array.isArray(data) ? data : []);
      }
    } catch {
      setFornecedores([]);
    }
  };

  const loadProdutos = async () => {
    try {
      const res = await fetch(API_PRODUTOS);
      if (res.ok) {
        const data = await res.json();
        setProdutos(Array.isArray(data) ? data : []);
      }
    } catch {
      setProdutos([]);
    }
  };

  useEffect(() => {
    loadFornecedores();
    loadProdutos();
  }, []);

  useEffect(() => {
    loadPrecos();
  }, [filtroDe, filtroAte]);

  const openNew = () => onOpen();

  const openEdit = (row) => {
    setEditingRow(row);
    onEditOpen();
  };

  const closeEdit = () => {
    onEditClose();
    setEditingRow(null);
  };

  const nomeFornecedor = (row) => {
    if (
      row.fornecedores &&
      typeof row.fornecedores === "object" &&
      row.fornecedores.nome
    )
      return row.fornecedores.nome;
    return row.fornecedor_id || "–";
  };
  const nomeProduto = (row) => {
    if (row.produtos && typeof row.produtos === "object" && row.produtos.nome)
      return row.produtos.nome;
    return row.produto_id || "–";
  };

  // Análise "Melhor Condição" do período: por produto, a oferta com menor preço + variação + preço médio
  const analiseMelhorCondicao = React.useMemo(() => {
    if (!lista.length) return [];
    const porProduto = {};
    lista.forEach((row) => {
      const prodId = row.produto_id;
      const nomeProd = nomeProduto(row);
      const nomeForn = nomeFornecedor(row);
      const valor = parseFloat(row.valor);
      const prazo = row.prazo_pagamento ?? 0;
      if (!porProduto[prodId]) {
        porProduto[prodId] = {
          nomeProduto: nomeProd,
          rows: [],
          minValor: null,
          minRow: null,
          maxValor: null,
        };
      }
      const p = porProduto[prodId];
      p.rows.push({ nomeFornecedor: nomeForn, valor, prazo });
      if (p.minValor == null || valor < p.minValor) {
        p.minValor = valor;
        p.minRow = { nomeFornecedor: nomeForn, valor, prazo };
      }
      if (p.maxValor == null || valor > p.maxValor) p.maxValor = valor;
    });
    return Object.keys(porProduto).map((prodId) => {
      const p = porProduto[prodId];
      const variacao =
        p.minValor > 0 && p.maxValor > p.minValor
          ? (((p.maxValor - p.minValor) / p.minValor) * 100).toFixed(0) + "%"
          : "–";
      const precoMedio =
        p.rows.length > 0
          ? p.rows.reduce((s, r) => s + r.valor, 0) / p.rows.length
          : null;
      return {
        fornecedor: p.minRow.nomeFornecedor,
        produto: p.nomeProduto,
        menorPreco: p.minValor,
        prazoPag: p.minRow.prazo,
        variacao,
        precoMedio,
      };
    });
  }, [lista]);

  // Análise "Condição Alternativa": por produto, a oferta com segundo menor preço
  const analiseCondicaoAlternativa = React.useMemo(() => {
    if (!lista.length) return [];
    const porProduto = {};
    lista.forEach((row) => {
      const prodId = row.produto_id;
      const nomeProd = nomeProduto(row);
      const nomeForn = nomeFornecedor(row);
      const valor = parseFloat(row.valor);
      const prazo = row.prazo_pagamento ?? 0;
      if (!porProduto[prodId]) {
        porProduto[prodId] = {
          nomeProduto: nomeProd,
          rows: [],
          maxValor: null,
          minValor: null,
        };
      }
      const p = porProduto[prodId];
      p.rows.push({ nomeFornecedor: nomeForn, valor, prazo });
      if (p.minValor == null || valor < p.minValor) p.minValor = valor;
      if (p.maxValor == null || valor > p.maxValor) p.maxValor = valor;
    });
    const result = [];
    Object.keys(porProduto).forEach((prodId) => {
      const p = porProduto[prodId];
      const sorted = [...p.rows].sort((a, b) => a.valor - b.valor);
      const second = sorted[1];
      if (!second) return;
      const variacao =
        p.minValor > 0 && p.maxValor > p.minValor
          ? (((p.maxValor - p.minValor) / p.minValor) * 100).toFixed(0) + "%"
          : "–";
      const precoMedio =
        p.rows.length > 0
          ? p.rows.reduce((s, r) => s + r.valor, 0) / p.rows.length
          : null;
      result.push({
        fornecedor: second.nomeFornecedor,
        produto: p.nomeProduto,
        menorPreco: second.valor,
        prazoPag: second.prazo,
        variacao,
        precoMedio,
      });
    });
    return result;
  }, [lista]);

  const periodoLabel =
    filtroDe && filtroAte
      ? `${formatarData(filtroDe)} a ${formatarData(filtroAte)}`
      : filtroDe
      ? `A partir de ${formatarData(filtroDe)}`
      : filtroAte
      ? `Até ${formatarData(filtroAte)}`
      : "Todo o período";

  const produtoFiltroId =
    produtoTabIndex === 0 ? null : produtos[produtoTabIndex - 1]?.id ?? null;
  const listaPorProduto = React.useMemo(
    () =>
      produtoFiltroId
        ? lista.filter((row) => row.produto_id === produtoFiltroId)
        : lista,
    [lista, produtoFiltroId]
  );

  /** Valor da célula para ordenação (comparável) */
  const getSortValue = (col, row) => {
    switch (col) {
      case "fornecedor":
        return nomeFornecedor(row) ?? "";
      case "produto":
        return nomeProduto(row) ?? "";
      case "data":
        return row.data ?? "";
      case "valor":
        return parseFloat(row.valor) ?? 0;
      case "prazo_pagamento":
        return Number(row.prazo_pagamento) ?? 0;
      default:
        return "";
    }
  };

  /** Valor exibido na célula (para filtro e exibição nas opções) */
  const getCellDisplay = (col, row) => {
    switch (col) {
      case "fornecedor":
        return nomeFornecedor(row) ?? "–";
      case "produto":
        return nomeProduto(row) ?? "–";
      case "data":
        return formatarData(row.data);
      case "valor":
        return row.valor != null
          ? `R$ ${parseFloat(row.valor).toLocaleString("pt-BR", {
              minimumFractionDigits: 4,
            })}`
          : "–";
      case "prazo_pagamento":
        return row.prazo_pagamento != null && row.prazo_pagamento !== ""
          ? String(row.prazo_pagamento)
          : "–";
      default:
        return "–";
    }
  };

  const distinctByColumn = React.useMemo(() => {
    const out = {
      fornecedor: [],
      produto: [],
      data: [],
      valor: [],
      prazo_pagamento: [],
    };
    const seen = {
      fornecedor: new Set(),
      produto: new Set(),
      data: new Set(),
      valor: new Set(),
      prazo_pagamento: new Set(),
    };
    listaPorProduto.forEach((row) => {
      ["fornecedor", "produto", "data", "valor", "prazo_pagamento"].forEach(
        (col) => {
          const d = getCellDisplay(col, row);
          if (!seen[col].has(d)) {
            seen[col].add(d);
            out[col].push(d);
          }
        }
      );
    });
    out.data.sort();
    out.fornecedor.sort();
    out.produto.sort();
    out.valor.sort((a, b) => {
      const na =
        parseFloat(
          String(a)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0;
      const nb =
        parseFloat(
          String(b)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0;
      return na - nb;
    });
    out.prazo_pagamento.sort((a, b) =>
      a === "–" ? -1 : b === "–" ? 1 : Number(a) - Number(b)
    );
    return out;
  }, [listaPorProduto]);

  const filteredList = React.useMemo(() => {
    return listaPorProduto.filter((row) => {
      return [
        "fornecedor",
        "produto",
        "data",
        "valor",
        "prazo_pagamento",
      ].every((col) => {
        const selected = columnFilters[col];
        if (!selected || selected.length === 0) return true;
        return selected.includes(getCellDisplay(col, row));
      });
    });
  }, [listaPorProduto, columnFilters]);

  const sortedList = React.useMemo(() => {
    if (!sortColumn) return filteredList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...filteredList].sort((a, b) => {
      const va = getSortValue(sortColumn, a);
      const vb = getSortValue(sortColumn, b);
      if (typeof va === "number" && typeof vb === "number")
        return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "pt-BR") * dir;
    });
  }, [filteredList, sortColumn, sortDirection]);

  const totalTablePages = Math.max(
    1,
    Math.ceil(sortedList.length / ITEMS_PER_PAGE)
  );

  const paginatedList = React.useMemo(() => {
    const start = (tablePage - 1) * ITEMS_PER_PAGE;
    return sortedList.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedList, tablePage]);

  useEffect(() => {
    setTablePage(1);
  }, [
    produtoTabIndex,
    columnFilters,
    sortColumn,
    sortDirection,
    filtroDe,
    filtroAte,
  ]);

  useEffect(() => {
    if (tablePage > totalTablePages) {
      setTablePage(totalTablePages);
    }
  }, [tablePage, totalTablePages]);

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortDirection("asc");
    }
  };

  const ANALISE_COLS = [
    "fornecedor",
    "produto",
    "menorPreco",
    "prazoPag",
    "variacao",
    "precoMedio",
  ];
  const getAnaliseDisplay = (col, row) => {
    switch (col) {
      case "fornecedor":
        return row.fornecedor ?? "–";
      case "produto":
        return row.produto ?? "–";
      case "menorPreco":
        return row.menorPreco != null
          ? `R$ ${Number(row.menorPreco).toLocaleString("pt-BR", {
              minimumFractionDigits: 4,
            })}`
          : "–";
      case "prazoPag":
        return row.prazoPag != null && row.prazoPag !== ""
          ? String(Number(row.prazoPag)).padStart(2, "0")
          : "–";
      case "variacao":
        return row.variacao ?? "–";
      case "precoMedio":
        return row.precoMedio != null
          ? `R$ ${Number(row.precoMedio).toLocaleString("pt-BR", {
              minimumFractionDigits: 4,
            })}`
          : "–";
      default:
        return "–";
    }
  };
  const getAnaliseSortValue = (col, row) => {
    switch (col) {
      case "fornecedor":
        return row.fornecedor ?? "";
      case "produto":
        return row.produto ?? "";
      case "menorPreco":
        return Number(row.menorPreco) ?? 0;
      case "prazoPag":
        return Number(row.prazoPag) ?? 0;
      case "variacao":
        return parseFloat(String(row.variacao).replace("%", "")) || 0;
      case "precoMedio":
        return Number(row.precoMedio) ?? 0;
      default:
        return "";
    }
  };

  const distinctAnaliseMelhor = React.useMemo(() => {
    const out = {
      fornecedor: [],
      produto: [],
      menorPreco: [],
      prazoPag: [],
      variacao: [],
      precoMedio: [],
    };
    const seen = {
      fornecedor: new Set(),
      produto: new Set(),
      menorPreco: new Set(),
      prazoPag: new Set(),
      variacao: new Set(),
      precoMedio: new Set(),
    };
    analiseMelhorCondicao.forEach((row) => {
      ANALISE_COLS.forEach((col) => {
        const d = getAnaliseDisplay(col, row);
        if (!seen[col].has(d)) {
          seen[col].add(d);
          out[col].push(d);
        }
      });
    });
    out.fornecedor.sort();
    out.produto.sort();
    out.menorPreco.sort(
      (a, b) =>
        (parseFloat(
          String(a)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0) -
        (parseFloat(
          String(b)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0)
    );
    out.prazoPag.sort((a, b) =>
      a === "–" ? -1 : b === "–" ? 1 : Number(a) - Number(b)
    );
    out.variacao.sort(
      (a, b) =>
        (parseFloat(String(a).replace("%", "")) || 0) -
        (parseFloat(String(b).replace("%", "")) || 0)
    );
    out.precoMedio.sort(
      (a, b) =>
        (parseFloat(
          String(a)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0) -
        (parseFloat(
          String(b)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0)
    );
    return out;
  }, [analiseMelhorCondicao]);

  const distinctAnaliseAlt = React.useMemo(() => {
    const out = {
      fornecedor: [],
      produto: [],
      menorPreco: [],
      prazoPag: [],
      variacao: [],
      precoMedio: [],
    };
    const seen = {
      fornecedor: new Set(),
      produto: new Set(),
      menorPreco: new Set(),
      prazoPag: new Set(),
      variacao: new Set(),
      precoMedio: new Set(),
    };
    analiseCondicaoAlternativa.forEach((row) => {
      ANALISE_COLS.forEach((col) => {
        const d = getAnaliseDisplay(col, row);
        if (!seen[col].has(d)) {
          seen[col].add(d);
          out[col].push(d);
        }
      });
    });
    out.fornecedor.sort();
    out.produto.sort();
    out.menorPreco.sort(
      (a, b) =>
        (parseFloat(
          String(a)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0) -
        (parseFloat(
          String(b)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0)
    );
    out.prazoPag.sort((a, b) =>
      a === "–" ? -1 : b === "–" ? 1 : Number(a) - Number(b)
    );
    out.variacao.sort(
      (a, b) =>
        (parseFloat(String(a).replace("%", "")) || 0) -
        (parseFloat(String(b).replace("%", "")) || 0)
    );
    out.precoMedio.sort(
      (a, b) =>
        (parseFloat(
          String(a)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0) -
        (parseFloat(
          String(b)
            .replace(/[^\d,.-]/g, "")
            .replace(",", ".")
        ) || 0)
    );
    return out;
  }, [analiseCondicaoAlternativa]);

  const filteredAnaliseMelhor = React.useMemo(() => {
    return analiseMelhorCondicao.filter((row) =>
      ANALISE_COLS.every((col) => {
        const selected = filtersAnaliseMelhor[col];
        if (!selected || selected.length === 0) return true;
        return selected.includes(getAnaliseDisplay(col, row));
      })
    );
  }, [analiseMelhorCondicao, filtersAnaliseMelhor]);

  const sortedAnaliseMelhor = React.useMemo(() => {
    if (!sortAnaliseMelhor) return filteredAnaliseMelhor;
    const dir = sortDirAnaliseMelhor === "asc" ? 1 : -1;
    return [...filteredAnaliseMelhor].sort((a, b) => {
      const va = getAnaliseSortValue(sortAnaliseMelhor, a);
      const vb = getAnaliseSortValue(sortAnaliseMelhor, b);
      if (typeof va === "number" && typeof vb === "number")
        return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "pt-BR") * dir;
    });
  }, [filteredAnaliseMelhor, sortAnaliseMelhor, sortDirAnaliseMelhor]);

  const filteredAnaliseAlt = React.useMemo(() => {
    return analiseCondicaoAlternativa.filter((row) =>
      ANALISE_COLS.every((col) => {
        const selected = filtersAnaliseAlt[col];
        if (!selected || selected.length === 0) return true;
        return selected.includes(getAnaliseDisplay(col, row));
      })
    );
  }, [analiseCondicaoAlternativa, filtersAnaliseAlt]);

  const sortedAnaliseAlt = React.useMemo(() => {
    if (!sortAnaliseAlt) return filteredAnaliseAlt;
    const dir = sortDirAnaliseAlt === "asc" ? 1 : -1;
    return [...filteredAnaliseAlt].sort((a, b) => {
      const va = getAnaliseSortValue(sortAnaliseAlt, a);
      const vb = getAnaliseSortValue(sortAnaliseAlt, b);
      if (typeof va === "number" && typeof vb === "number")
        return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "pt-BR") * dir;
    });
  }, [filteredAnaliseAlt, sortAnaliseAlt, sortDirAnaliseAlt]);

  const handleSortAnaliseMelhor = (col) => {
    if (sortAnaliseMelhor === col)
      setSortDirAnaliseMelhor((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortAnaliseMelhor(col);
      setSortDirAnaliseMelhor("asc");
    }
  };
  const handleSortAnaliseAlt = (col) => {
    if (sortAnaliseAlt === col)
      setSortDirAnaliseAlt((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortAnaliseAlt(col);
      setSortDirAnaliseAlt("asc");
    }
  };

  /** Dados para o gráfico: evolução do preço médio por produto ao longo do tempo */
  const chartData = React.useMemo(() => {
    if (!lista.length) return { data: [], productKeys: [] };
    const byDate = {};
    const productNamesSet = new Set();
    lista.forEach((row) => {
      const d = row.data;
      const nome = nomeProduto(row);
      const valor = parseFloat(row.valor);
      if (!d || Number.isNaN(valor)) return;
      productNamesSet.add(nome);
      if (!byDate[d]) byDate[d] = {};
      if (!byDate[d][nome]) byDate[d][nome] = { sum: 0, count: 0 };
      byDate[d][nome].sum += valor;
      byDate[d][nome].count += 1;
    });
    const sortedDates = Object.keys(byDate).sort();
    const productKeys = Array.from(productNamesSet).sort();
    const data = sortedDates.map((d) => {
      const point = { data: formatarData(d), dataRaw: d };
      productKeys.forEach((nome) => {
        const agg = byDate[d][nome];
        point[nome] = agg ? agg.sum / agg.count : null;
      });
      return point;
    });
    return { data, productKeys };
  }, [lista]);

  /** Dados para o gráfico: evolução do preço médio por fornecedor ao longo do tempo */
  const chartDataByFornecedor = React.useMemo(() => {
    if (!lista.length) return { data: [], fornecedorKeys: [] };
    const byDate = {};
    const fornecedorNamesSet = new Set();
    lista.forEach((row) => {
      const d = row.data;
      const nome = nomeFornecedor(row);
      const valor = parseFloat(row.valor);
      if (!d || Number.isNaN(valor)) return;
      fornecedorNamesSet.add(nome);
      if (!byDate[d]) byDate[d] = {};
      if (!byDate[d][nome]) byDate[d][nome] = { sum: 0, count: 0 };
      byDate[d][nome].sum += valor;
      byDate[d][nome].count += 1;
    });
    const sortedDates = Object.keys(byDate).sort();
    const fornecedorKeys = Array.from(fornecedorNamesSet).sort();
    const data = sortedDates.map((d) => {
      const point = { data: formatarData(d), dataRaw: d };
      fornecedorKeys.forEach((nome) => {
        const agg = byDate[d][nome];
        point[nome] = agg ? agg.sum / agg.count : null;
      });
      return point;
    });
    return { data, fornecedorKeys };
  }, [lista]);

  const isSingleDayPeriod =
    (Boolean(filtroDe && filtroAte && filtroDe === filtroAte)) ||
    chartData.data.length <= 1;

  const singleDayProductBars = React.useMemo(() => {
    if (!chartData.data.length) return [];
    const point = chartData.data[0];
    return chartData.productKeys
      .map((nome, i) => ({
        nome,
        preco: point[nome] ?? null,
        cor: CORES_GRAFICO[i % CORES_GRAFICO.length],
      }))
      .filter((d) => d.preco != null);
  }, [chartData]);

  const singleDayFornecedorBars = React.useMemo(() => {
    if (!chartDataByFornecedor.data.length) return [];
    const point = chartDataByFornecedor.data[0];
    return chartDataByFornecedor.fornecedorKeys
      .map((nome, i) => ({
        nome,
        preco: point[nome] ?? null,
        cor: CORES_GRAFICO[i % CORES_GRAFICO.length],
      }))
      .filter((d) => d.preco != null);
  }, [chartDataByFornecedor]);

  const visibleSingleDayProductBars = React.useMemo(
    () =>
      singleDayProductBars.filter(
        (d) =>
          selectedProductKeys.size === 0 || selectedProductKeys.has(d.nome)
      ),
    [singleDayProductBars, selectedProductKeys]
  );

  const visibleSingleDayFornecedorBars = React.useMemo(
    () =>
      singleDayFornecedorBars.filter(
        (d) =>
          selectedFornecedorKeys.size === 0 ||
          selectedFornecedorKeys.has(d.nome)
      ),
    [singleDayFornecedorBars, selectedFornecedorKeys]
  );

  const dominioProdutoBars = calcDominioPreco(
    visibleSingleDayProductBars.map((d) => d.preco)
  );
  const dominioFornecedorBars = calcDominioPreco(
    visibleSingleDayFornecedorBars.map((d) => d.preco)
  );

  const showProdutoColumn = produtoTabIndex === 0;

  const ANALISE_TABLE_COLUMNS = [
    { key: "fornecedor", label: "Fornecedor", align: "center" },
    { key: "produto", label: "Produto", align: "left" },
    { key: "menorPreco", label: "Menor preço (R$)", align: "center" },
    { key: "prazoPag", label: "Prazo pag.", align: "center" },
    { key: "variacao", label: "Variação %", align: "center" },
    { key: "precoMedio", label: "Preço médio (R$)", align: "center" },
  ];

  return (
    <Flex direction="column" minH="100vh" bg="gray.50">
      <Box flex="1" py={6} px={{ base: 4, md: 6 }}>
        <Box maxW="1400px" mx="auto">
          <Flex
            align="center"
            justify="space-between"
            mb={6}
            flexWrap="wrap"
            gap={4}
          >
            <Button
              leftIcon={<FaArrowLeft />}
              variant="outline"
              colorScheme="blue"
              onClick={() => navigate("/home")}
            >
              Voltar
            </Button>
            <Heading size="lg" color="#007abe">
              Histórico de Preços
            </Heading>
            <Button
              leftIcon={<FaPlus />}
              bg="#007abe"
              color="white"
              onClick={openNew}
              _hover={{ bg: "#0066a3" }}
            >
              Registrar preço
            </Button>
          </Flex>

          <Flex gap={4} mb={4} flexWrap="wrap">
            <FormControl maxW="180px">
              <FormLabel fontSize="sm">Data de</FormLabel>
              <Input
                type="date"
                value={filtroDe}
                onChange={(e) => setFiltroDe(e.target.value)}
                size="sm"
              />
            </FormControl>
            <FormControl maxW="180px">
              <FormLabel fontSize="sm">Data até</FormLabel>
              <Input
                type="date"
                value={filtroAte}
                onChange={(e) => setFiltroAte(e.target.value)}
                size="sm"
              />
            </FormControl>
          </Flex>

          {/* Análise do período – Melhor Condição (estilo Excel) */}
          {lista.length > 0 && (
            <Box mb={8}>
              <Heading size="sm" color="gray.700" mb={3}>
                Análise do período: {periodoLabel}
              </Heading>

              <Flex mb={6} align="stretch">
                <Box
                  bg="#007abe"
                  color="white"
                  width="32px"
                  minHeight="140px"
                  borderLeftRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    letterSpacing="wider"
                    transform="rotate(-90deg)"
                    whiteSpace="nowrap"
                  >
                    MELHOR CONDIÇÃO
                  </Text>
                </Box>
                <TableContainer
                  flex={1}
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderLeftWidth="0"
                  borderRadius="0"
                  borderTopRightRadius="lg"
                  borderBottomRightRadius="lg"
                  boxShadow="md"
                  overflowX="auto"
                >
                  <Table variant="simple" size="sm">
                    <Thead bg="#007abe">
                      <Tr>
                        {ANALISE_TABLE_COLUMNS.map(({ key, label, align }) => (
                          <Th
                            key={key}
                            color="white"
                            textAlign={align}
                            verticalAlign="middle"
                          >
                            <HStack
                              spacing={1}
                              justify={
                                align === "center"
                                  ? "center"
                                  : align === "right"
                                  ? "flex-end"
                                  : "space-between"
                              }
                            >
                              <Text as="span">{label}</Text>
                              <HStack spacing={0}>
                                <IconButton
                                  aria-label={`Ordenar ${label}`}
                                  size="xs"
                                  variant="ghost"
                                  color="white"
                                  _hover={{ bg: "whiteAlpha.300" }}
                                  icon={
                                    sortAnaliseMelhor === key ? (
                                      sortDirAnaliseMelhor === "asc" ? (
                                        <FaSortUp />
                                      ) : (
                                        <FaSortDown />
                                      )
                                    ) : (
                                      <FaSort />
                                    )
                                  }
                                  onClick={() => handleSortAnaliseMelhor(key)}
                                />
                                <Popover placement="bottom-start" isLazy>
                                  <PopoverTrigger>
                                    <IconButton
                                      aria-label={`Filtrar ${label}`}
                                      size="xs"
                                      variant="ghost"
                                      color="white"
                                      _hover={{ bg: "whiteAlpha.300" }}
                                      icon={<FaFilter />}
                                    />
                                  </PopoverTrigger>
                                  <PopoverContent
                                    minW="180px"
                                    maxH="280px"
                                    overflowY="auto"
                                    bg="white"
                                    color="gray.800"
                                    borderColor="gray.200"
                                  >
                                    <PopoverHeader
                                      fontWeight="semibold"
                                      color="gray.800"
                                    >
                                      Filtrar {label}
                                    </PopoverHeader>
                                    <PopoverBody color="gray.800">
                                      <CheckboxGroup
                                        value={filtersAnaliseMelhor[key] || []}
                                        onChange={(vals) =>
                                          setFiltersAnaliseMelhor((f) => ({
                                            ...f,
                                            [key]: vals,
                                          }))
                                        }
                                      >
                                        <Flex direction="column" gap={1}>
                                          {(
                                            distinctAnaliseMelhor[key] || []
                                          ).map((v) => (
                                            <Checkbox
                                              key={v}
                                              value={v}
                                              size="sm"
                                              colorScheme="blue"
                                              sx={{
                                                "& .chakra-checkbox__label": {
                                                  color: "gray.800",
                                                },
                                              }}
                                            >
                                              {v}
                                            </Checkbox>
                                          ))}
                                        </Flex>
                                      </CheckboxGroup>
                                      <Button
                                        size="xs"
                                        mt={2}
                                        onClick={() =>
                                          setFiltersAnaliseMelhor((f) => ({
                                            ...f,
                                            [key]: [],
                                          }))
                                        }
                                      >
                                        Limpar
                                      </Button>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Popover>
                              </HStack>
                            </HStack>
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {sortedAnaliseMelhor.map((item, idx) => (
                        <Tr key={idx}>
                          <Td
                            fontWeight="medium"
                            textAlign="center"
                            verticalAlign="middle"
                          >
                            {item.fornecedor}
                          </Td>
                          <Td textAlign="left" verticalAlign="middle">
                            <Badge
                              colorScheme="blue"
                              bg="#007abe"
                              color="white"
                              px={2}
                              py={1}
                            >
                              {item.produto}
                            </Badge>
                          </Td>
                          <Td textAlign="center" verticalAlign="middle">
                            {getAnaliseDisplay("menorPreco", item)}
                          </Td>
                          <Td textAlign="center" verticalAlign="middle">
                            {getAnaliseDisplay("prazoPag", item)}
                          </Td>
                          <Td textAlign="center" verticalAlign="middle">
                            {item.variacao}
                          </Td>
                          <Td textAlign="center" verticalAlign="middle">
                            {getAnaliseDisplay("precoMedio", item)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Flex>
              <Text fontSize="xs" color="gray.500" mb={6}>
                Por produto, a oferta com menor preço no período. Variação =
                amplitude (máx. − mín.) em % sobre o menor preço.
              </Text>

              {/* Condição Alternativa */}
              <Flex align="stretch">
                <Box
                  bg="#007abe"
                  color="white"
                  width="32px"
                  minHeight="140px"
                  borderLeftRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    letterSpacing="wider"
                    transform="rotate(-90deg)"
                    whiteSpace="nowrap"
                  >
                    CONDIÇÃO ALTERNATIVA
                  </Text>
                </Box>
                <TableContainer
                  flex={1}
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderLeftWidth="0"
                  borderRadius="0"
                  borderTopRightRadius="lg"
                  borderBottomRightRadius="lg"
                  boxShadow="md"
                  overflowX="auto"
                >
                  <Table variant="simple" size="sm">
                    <Thead bg="#007abe">
                      <Tr>
                        {ANALISE_TABLE_COLUMNS.map(({ key, label, align }) => (
                          <Th
                            key={key}
                            color="white"
                            textAlign={align}
                            verticalAlign="middle"
                          >
                            <HStack
                              spacing={1}
                              justify={
                                align === "center"
                                  ? "center"
                                  : align === "right"
                                  ? "flex-end"
                                  : "space-between"
                              }
                            >
                              <Text as="span">{label}</Text>
                              <HStack spacing={0}>
                                <IconButton
                                  aria-label={`Ordenar ${label}`}
                                  size="xs"
                                  variant="ghost"
                                  color="white"
                                  _hover={{ bg: "whiteAlpha.300" }}
                                  icon={
                                    sortAnaliseAlt === key ? (
                                      sortDirAnaliseAlt === "asc" ? (
                                        <FaSortUp />
                                      ) : (
                                        <FaSortDown />
                                      )
                                    ) : (
                                      <FaSort />
                                    )
                                  }
                                  onClick={() => handleSortAnaliseAlt(key)}
                                />
                                <Popover placement="bottom-start" isLazy>
                                  <PopoverTrigger>
                                    <IconButton
                                      aria-label={`Filtrar ${label}`}
                                      size="xs"
                                      variant="ghost"
                                      color="white"
                                      _hover={{ bg: "whiteAlpha.300" }}
                                      icon={<FaFilter />}
                                    />
                                  </PopoverTrigger>
                                  <PopoverContent
                                    minW="180px"
                                    maxH="280px"
                                    overflowY="auto"
                                    bg="white"
                                    color="gray.800"
                                    borderColor="gray.200"
                                  >
                                    <PopoverHeader
                                      fontWeight="semibold"
                                      color="gray.800"
                                    >
                                      Filtrar {label}
                                    </PopoverHeader>
                                    <PopoverBody color="gray.800">
                                      <CheckboxGroup
                                        value={filtersAnaliseAlt[key] || []}
                                        onChange={(vals) =>
                                          setFiltersAnaliseAlt((f) => ({
                                            ...f,
                                            [key]: vals,
                                          }))
                                        }
                                      >
                                        <Flex direction="column" gap={1}>
                                          {(distinctAnaliseAlt[key] || []).map(
                                            (v) => (
                                              <Checkbox
                                                key={v}
                                                value={v}
                                                size="sm"
                                                colorScheme="blue"
                                                sx={{
                                                  "& .chakra-checkbox__label": {
                                                    color: "gray.800",
                                                  },
                                                }}
                                              >
                                                {v}
                                              </Checkbox>
                                            )
                                          )}
                                        </Flex>
                                      </CheckboxGroup>
                                      <Button
                                        size="xs"
                                        mt={2}
                                        onClick={() =>
                                          setFiltersAnaliseAlt((f) => ({
                                            ...f,
                                            [key]: [],
                                          }))
                                        }
                                      >
                                        Limpar
                                      </Button>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Popover>
                              </HStack>
                            </HStack>
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {sortedAnaliseAlt.map((item, idx) => (
                        <Tr key={idx}>
                          <Td
                            fontWeight="medium"
                            textAlign="center"
                            verticalAlign="middle"
                          >
                            {item.fornecedor}
                          </Td>
                          <Td textAlign="left" verticalAlign="middle">
                            <Badge
                              colorScheme="blue"
                              bg="#007abe"
                              color="white"
                              px={2}
                              py={1}
                            >
                              {item.produto}
                            </Badge>
                          </Td>
                          <Td textAlign="center" verticalAlign="middle">
                            {getAnaliseDisplay("menorPreco", item)}
                          </Td>
                          <Td textAlign="center" verticalAlign="middle">
                            {getAnaliseDisplay("prazoPag", item)}
                          </Td>
                          <Td textAlign="center" verticalAlign="middle">
                            {item.variacao}
                          </Td>
                          <Td textAlign="center" verticalAlign="middle">
                            {getAnaliseDisplay("precoMedio", item)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Flex>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Por produto, a oferta com segundo menor preço no período
                (alternativa à melhor).
              </Text>
            </Box>
          )}

          {/* Gráfico: evolução dos preços por produto */}
          {lista.length > 0 && chartData.data.length > 0 && (
            <Box mb={8}>
              <Heading size="sm" color="gray.700" mb={3}>
                {isSingleDayPeriod
                  ? `Comparativo de preços por produto (${periodoLabel})`
                  : `Evolução dos preços (${periodoLabel})`}
              </Heading>
              <Box
                bg="white"
                borderRadius="xl"
                boxShadow="md"
                p={4}
                borderWidth="1px"
                borderColor="gray.200"
              >
                <ResponsiveContainer
                  width="100%"
                  height={isSingleDayPeriod ? 420 : 360}
                >
                  {isSingleDayPeriod ? (
                    <BarChart
                      data={visibleSingleDayProductBars}
                      margin={{ top: 8, right: 24, left: 8, bottom: 72 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="gray.200" />
                      <XAxis
                        dataKey="nome"
                        tick={{ fontSize: 11 }}
                        stroke="gray.600"
                        interval={0}
                        angle={-30}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis
                        domain={dominioProdutoBars}
                        tick={{ fontSize: 12 }}
                        stroke="gray.600"
                        tickFormatter={formatPrecoEixo}
                      />
                      <Tooltip
                        formatter={(value) =>
                          value != null
                            ? [`R$ ${formatPrecoValor(value)}`, "Preço médio"]
                            : ["–", ""]
                        }
                        labelFormatter={(label) => label}
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid",
                          borderColor: "gray.200",
                          borderRadius: "md",
                        }}
                      />
                      <Bar dataKey="preco" radius={[6, 6, 0, 0]} maxBarSize={72}>
                        {visibleSingleDayProductBars.map((entry) => (
                          <Cell key={entry.nome} fill={entry.cor} />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : (
                    <LineChart
                      data={chartData.data}
                      margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="gray.200" />
                      <XAxis
                        dataKey="data"
                        tick={{ fontSize: 12 }}
                        stroke="gray.600"
                      />
                      <YAxis
                        domain={[3, 6.5]}
                        tick={{ fontSize: 12 }}
                        stroke="gray.600"
                        tickFormatter={formatPrecoEixo}
                      />
                      <Tooltip
                        formatter={(value) =>
                          value != null
                            ? [`R$ ${formatPrecoValor(value)}`, ""]
                            : ["–", ""]
                        }
                        labelFormatter={(label) => `Data: ${label}`}
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid",
                          borderColor: "gray.200",
                          borderRadius: "md",
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: "12px" }}
                        content={() => (
                          <Flex
                            flexWrap="wrap"
                            gap={2}
                            justify="center"
                            as="ul"
                            listStyleType="none"
                            fontSize="12px"
                            mt={2}
                          >
                            {chartData.productKeys.map((nome, i) => (
                              <Box
                                as="li"
                                key={nome}
                                onClick={() => toggleProductKey(nome)}
                                cursor="pointer"
                                opacity={
                                  selectedProductKeys.size === 0 ||
                                  selectedProductKeys.has(nome)
                                    ? 1
                                    : 0.5
                                }
                                _hover={{ textDecoration: "underline" }}
                              >
                                <Box
                                  as="span"
                                  w="12px"
                                  h="12px"
                                  display="inline-block"
                                  bg={
                                    CORES_GRAFICO[i % CORES_GRAFICO.length]
                                  }
                                  mr={2}
                                  borderRadius="sm"
                                  verticalAlign="middle"
                                />
                                {nome}
                              </Box>
                            ))}
                          </Flex>
                        )}
                      />
                      {chartData.productKeys
                        .filter(
                          (nome) =>
                            selectedProductKeys.size === 0 ||
                            selectedProductKeys.has(nome)
                        )
                        .map((nome) => (
                          <Line
                            key={nome}
                            type="monotone"
                            dataKey={nome}
                            stroke={
                              CORES_GRAFICO[
                                chartData.productKeys.indexOf(nome) %
                                  CORES_GRAFICO.length
                              ]
                            }
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                            connectNulls
                          />
                        ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
                {isSingleDayPeriod && (
                  <Flex
                    flexWrap="wrap"
                    gap={2}
                    justify="center"
                    fontSize="12px"
                    mt={3}
                  >
                    {chartData.productKeys.map((nome, i) => (
                      <Box
                        key={nome}
                        onClick={() => toggleProductKey(nome)}
                        cursor="pointer"
                        opacity={
                          selectedProductKeys.size === 0 ||
                          selectedProductKeys.has(nome)
                            ? 1
                            : 0.5
                        }
                        _hover={{ textDecoration: "underline" }}
                      >
                        <Box
                          as="span"
                          w="12px"
                          h="12px"
                          display="inline-block"
                          bg={CORES_GRAFICO[i % CORES_GRAFICO.length]}
                          mr={2}
                          borderRadius="sm"
                          verticalAlign="middle"
                        />
                        {nome}
                      </Box>
                    ))}
                  </Flex>
                )}
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {isSingleDayPeriod
                    ? "Preço médio de cada produto na data selecionada (média de todos os fornecedores). 1º clique na legenda: exibe só o produto clicado; cliques seguintes acrescentam ou removem da seleção."
                    : "Preço médio por produto em cada data (considerando todos os fornecedores). 1º clique: exibe só o produto clicado; cliques seguintes acrescentam ou removem da seleção."}
                </Text>
              </Box>
            </Box>
          )}

          {/* Gráfico: evolução dos preços por fornecedor */}
          {lista.length > 0 && chartDataByFornecedor.data.length > 0 && (
            <Box mb={8}>
              <Heading size="sm" color="gray.700" mb={3}>
                {isSingleDayPeriod
                  ? `Comparativo de preços por fornecedor (${periodoLabel})`
                  : `Evolução dos preços por fornecedor (${periodoLabel})`}
              </Heading>
              <Box
                bg="white"
                borderRadius="xl"
                boxShadow="md"
                p={4}
                borderWidth="1px"
                borderColor="gray.200"
              >
                <ResponsiveContainer
                  width="100%"
                  height={isSingleDayPeriod ? 420 : 360}
                >
                  {isSingleDayPeriod ? (
                    <BarChart
                      data={visibleSingleDayFornecedorBars}
                      margin={{ top: 8, right: 24, left: 8, bottom: 72 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="gray.200" />
                      <XAxis
                        dataKey="nome"
                        tick={{ fontSize: 11 }}
                        stroke="gray.600"
                        interval={0}
                        angle={-30}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis
                        domain={dominioFornecedorBars}
                        tick={{ fontSize: 12 }}
                        stroke="gray.600"
                        tickFormatter={formatPrecoEixo}
                      />
                      <Tooltip
                        formatter={(value) =>
                          value != null
                            ? [`R$ ${formatPrecoValor(value)}`, "Preço médio"]
                            : ["–", ""]
                        }
                        labelFormatter={(label) => label}
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid",
                          borderColor: "gray.200",
                          borderRadius: "md",
                        }}
                      />
                      <Bar dataKey="preco" radius={[6, 6, 0, 0]} maxBarSize={72}>
                        {visibleSingleDayFornecedorBars.map((entry) => (
                          <Cell key={entry.nome} fill={entry.cor} />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : (
                    <LineChart
                      data={chartDataByFornecedor.data}
                      margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="gray.200" />
                      <XAxis
                        dataKey="data"
                        tick={{ fontSize: 12 }}
                        stroke="gray.600"
                      />
                      <YAxis
                        domain={[3, 6.5]}
                        tick={{ fontSize: 12 }}
                        stroke="gray.600"
                        tickFormatter={formatPrecoEixo}
                      />
                      <Tooltip
                        formatter={(value) =>
                          value != null
                            ? [`R$ ${formatPrecoValor(value)}`, ""]
                            : ["–", ""]
                        }
                        labelFormatter={(label) => `Data: ${label}`}
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid",
                          borderColor: "gray.200",
                          borderRadius: "md",
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: "12px" }}
                        content={() => (
                          <Flex
                            flexWrap="wrap"
                            gap={2}
                            justify="center"
                            as="ul"
                            listStyleType="none"
                            fontSize="12px"
                            mt={2}
                          >
                            {chartDataByFornecedor.fornecedorKeys.map(
                              (nome, i) => (
                                <Box
                                  as="li"
                                  key={nome}
                                  onClick={() => toggleFornecedorKey(nome)}
                                  cursor="pointer"
                                  opacity={
                                    selectedFornecedorKeys.size === 0 ||
                                    selectedFornecedorKeys.has(nome)
                                      ? 1
                                      : 0.5
                                  }
                                  _hover={{ textDecoration: "underline" }}
                                >
                                  <Box
                                    as="span"
                                    w="12px"
                                    h="12px"
                                    display="inline-block"
                                    bg={
                                      CORES_GRAFICO[i % CORES_GRAFICO.length]
                                    }
                                    mr={2}
                                    borderRadius="sm"
                                    verticalAlign="middle"
                                  />
                                  {nome}
                                </Box>
                              )
                            )}
                          </Flex>
                        )}
                      />
                      {chartDataByFornecedor.fornecedorKeys
                        .filter(
                          (nome) =>
                            selectedFornecedorKeys.size === 0 ||
                            selectedFornecedorKeys.has(nome)
                        )
                        .map((nome) => (
                          <Line
                            key={nome}
                            type="monotone"
                            dataKey={nome}
                            stroke={
                              CORES_GRAFICO[
                                chartDataByFornecedor.fornecedorKeys.indexOf(
                                  nome
                                ) % CORES_GRAFICO.length
                              ]
                            }
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                            connectNulls
                          />
                        ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
                {isSingleDayPeriod && (
                  <Flex
                    flexWrap="wrap"
                    gap={2}
                    justify="center"
                    fontSize="12px"
                    mt={3}
                  >
                    {chartDataByFornecedor.fornecedorKeys.map((nome, i) => (
                      <Box
                        key={nome}
                        onClick={() => toggleFornecedorKey(nome)}
                        cursor="pointer"
                        opacity={
                          selectedFornecedorKeys.size === 0 ||
                          selectedFornecedorKeys.has(nome)
                            ? 1
                            : 0.5
                        }
                        _hover={{ textDecoration: "underline" }}
                      >
                        <Box
                          as="span"
                          w="12px"
                          h="12px"
                          display="inline-block"
                          bg={CORES_GRAFICO[i % CORES_GRAFICO.length]}
                          mr={2}
                          borderRadius="sm"
                          verticalAlign="middle"
                        />
                        {nome}
                      </Box>
                    ))}
                  </Flex>
                )}
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {isSingleDayPeriod
                    ? "Preço médio de cada fornecedor na data selecionada (média de todos os produtos do fornecedor). 1º clique na legenda: exibe só o fornecedor clicado; cliques seguintes acrescentam ou removem da seleção."
                    : "Preço médio de cada fornecedor em cada data (média de todos os produtos do fornecedor). 1º clique: exibe só o fornecedor clicado; cliques seguintes acrescentam ou removem da seleção."}
                </Text>
              </Box>
            </Box>
          )}

          <Tabs
            variant="enclosed-colored"
            colorScheme="blue"
            index={Math.min(produtoTabIndex, produtos.length)}
            onChange={setProdutoTabIndex}
            mb={4}
          >
            <TabList flexWrap="wrap" gap={1}>
              <Tab>Todos</Tab>
              {produtos.map((p) => (
                <Tab key={p.id}>{p.nome}</Tab>
              ))}
            </TabList>
            <TabPanels>
              {[null, ...produtos].map((p) => (
                <TabPanel key={p?.id ?? "todos"} px={0} pt={4}>
                  <TableContainer
                    bg="white"
                    borderRadius="xl"
                    boxShadow="md"
                    overflowX="auto"
                  >
                    {loading ? (
                      <Center py={12}>
                        <Spinner size="xl" color="#007abe" />
                      </Center>
                    ) : (
                      <Table variant="simple" size="sm">
                        <Thead bg="#007abe">
                          <Tr>
                            <Th color="white">
                              <HStack spacing={1} justify="space-between">
                                <Text as="span">Fornecedor</Text>
                                <HStack spacing={0}>
                                  <IconButton
                                    aria-label="Ordenar fornecedor"
                                    size="xs"
                                    variant="ghost"
                                    color="white"
                                    _hover={{ bg: "whiteAlpha.300" }}
                                    icon={
                                      sortColumn === "fornecedor" ? (
                                        sortDirection === "asc" ? (
                                          <FaSortUp />
                                        ) : (
                                          <FaSortDown />
                                        )
                                      ) : (
                                        <FaSort />
                                      )
                                    }
                                    onClick={() => handleSort("fornecedor")}
                                  />
                                  <Popover placement="bottom-start" isLazy>
                                    <PopoverTrigger>
                                      <IconButton
                                        aria-label="Filtrar fornecedor"
                                        size="xs"
                                        variant="ghost"
                                        color="white"
                                        _hover={{ bg: "whiteAlpha.300" }}
                                        icon={<FaFilter />}
                                      />
                                    </PopoverTrigger>
                                    <PopoverContent
                                      minW="200px"
                                      maxH="280px"
                                      overflowY="auto"
                                      bg="white"
                                      color="gray.800"
                                      borderColor="gray.200"
                                    >
                                      <PopoverHeader
                                        fontWeight="semibold"
                                        color="gray.800"
                                      >
                                        Filtrar Fornecedor
                                      </PopoverHeader>
                                      <PopoverBody color="gray.800">
                                        <CheckboxGroup
                                          value={columnFilters.fornecedor}
                                          onChange={(vals) =>
                                            setColumnFilters((f) => ({
                                              ...f,
                                              fornecedor: vals,
                                            }))
                                          }
                                        >
                                          <Flex direction="column" gap={1}>
                                            {distinctByColumn.fornecedor.map(
                                              (v) => (
                                                <Checkbox
                                                  key={v}
                                                  value={v}
                                                  size="sm"
                                                  colorScheme="blue"
                                                  sx={{
                                                    "& .chakra-checkbox__label":
                                                      { color: "gray.800" },
                                                  }}
                                                >
                                                  {v}
                                                </Checkbox>
                                              )
                                            )}
                                          </Flex>
                                        </CheckboxGroup>
                                        <Button
                                          size="xs"
                                          mt={2}
                                          onClick={() =>
                                            setColumnFilters((f) => ({
                                              ...f,
                                              fornecedor: [],
                                            }))
                                          }
                                        >
                                          Limpar
                                        </Button>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Popover>
                                </HStack>
                              </HStack>
                            </Th>
                            {showProdutoColumn && (
                              <Th color="white">
                                <HStack spacing={1} justify="space-between">
                                  <Text as="span">Produto</Text>
                                  <HStack spacing={0}>
                                    <IconButton
                                      aria-label="Ordenar produto"
                                      size="xs"
                                      variant="ghost"
                                      color="white"
                                      _hover={{ bg: "whiteAlpha.300" }}
                                      icon={
                                        sortColumn === "produto" ? (
                                          sortDirection === "asc" ? (
                                            <FaSortUp />
                                          ) : (
                                            <FaSortDown />
                                          )
                                        ) : (
                                          <FaSort />
                                        )
                                      }
                                      onClick={() => handleSort("produto")}
                                    />
                                    <Popover placement="bottom-start" isLazy>
                                      <PopoverTrigger>
                                        <IconButton
                                          aria-label="Filtrar produto"
                                          size="xs"
                                          variant="ghost"
                                          color="white"
                                          _hover={{ bg: "whiteAlpha.300" }}
                                          icon={<FaFilter />}
                                        />
                                      </PopoverTrigger>
                                      <PopoverContent
                                        minW="200px"
                                        maxH="280px"
                                        overflowY="auto"
                                        bg="white"
                                        color="gray.800"
                                        borderColor="gray.200"
                                      >
                                        <PopoverHeader
                                          fontWeight="semibold"
                                          color="gray.800"
                                        >
                                          Filtrar Produto
                                        </PopoverHeader>
                                        <PopoverBody color="gray.800">
                                          <CheckboxGroup
                                            value={columnFilters.produto}
                                            onChange={(vals) =>
                                              setColumnFilters((f) => ({
                                                ...f,
                                                produto: vals,
                                              }))
                                            }
                                          >
                                            <Flex direction="column" gap={1}>
                                              {distinctByColumn.produto.map(
                                                (v) => (
                                                  <Checkbox
                                                    key={v}
                                                    value={v}
                                                    size="sm"
                                                    colorScheme="blue"
                                                    sx={{
                                                      "& .chakra-checkbox__label":
                                                        { color: "gray.800" },
                                                    }}
                                                  >
                                                    {v}
                                                  </Checkbox>
                                                )
                                              )}
                                            </Flex>
                                          </CheckboxGroup>
                                          <Button
                                            size="xs"
                                            mt={2}
                                            onClick={() =>
                                              setColumnFilters((f) => ({
                                                ...f,
                                                produto: [],
                                              }))
                                            }
                                          >
                                            Limpar
                                          </Button>
                                        </PopoverBody>
                                      </PopoverContent>
                                    </Popover>
                                  </HStack>
                                </HStack>
                              </Th>
                            )}
                            <Th color="white">
                              <HStack spacing={1} justify="space-between">
                                <Text as="span">Data</Text>
                                <HStack spacing={0}>
                                  <IconButton
                                    aria-label="Ordenar data"
                                    size="xs"
                                    variant="ghost"
                                    color="white"
                                    _hover={{ bg: "whiteAlpha.300" }}
                                    icon={
                                      sortColumn === "data" ? (
                                        sortDirection === "asc" ? (
                                          <FaSortUp />
                                        ) : (
                                          <FaSortDown />
                                        )
                                      ) : (
                                        <FaSort />
                                      )
                                    }
                                    onClick={() => handleSort("data")}
                                  />
                                  <Popover placement="bottom-start" isLazy>
                                    <PopoverTrigger>
                                      <IconButton
                                        aria-label="Filtrar data"
                                        size="xs"
                                        variant="ghost"
                                        color="white"
                                        _hover={{ bg: "whiteAlpha.300" }}
                                        icon={<FaFilter />}
                                      />
                                    </PopoverTrigger>
                                    <PopoverContent
                                      minW="160px"
                                      maxH="280px"
                                      overflowY="auto"
                                      bg="white"
                                      color="gray.800"
                                      borderColor="gray.200"
                                    >
                                      <PopoverHeader
                                        fontWeight="semibold"
                                        color="gray.800"
                                      >
                                        Filtrar Data
                                      </PopoverHeader>
                                      <PopoverBody color="gray.800">
                                        <CheckboxGroup
                                          value={columnFilters.data}
                                          onChange={(vals) =>
                                            setColumnFilters((f) => ({
                                              ...f,
                                              data: vals,
                                            }))
                                          }
                                        >
                                          <Flex direction="column" gap={1}>
                                            {distinctByColumn.data.map((v) => (
                                              <Checkbox
                                                key={v}
                                                value={v}
                                                size="sm"
                                                colorScheme="blue"
                                                sx={{
                                                  "& .chakra-checkbox__label": {
                                                    color: "gray.800",
                                                  },
                                                }}
                                              >
                                                {v}
                                              </Checkbox>
                                            ))}
                                          </Flex>
                                        </CheckboxGroup>
                                        <Button
                                          size="xs"
                                          mt={2}
                                          onClick={() =>
                                            setColumnFilters((f) => ({
                                              ...f,
                                              data: [],
                                            }))
                                          }
                                        >
                                          Limpar
                                        </Button>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Popover>
                                </HStack>
                              </HStack>
                            </Th>
                            <Th color="white">
                              <HStack spacing={1} justify="space-between">
                                <Text as="span">Valor (R$)</Text>
                                <HStack spacing={0}>
                                  <IconButton
                                    aria-label="Ordenar valor"
                                    size="xs"
                                    variant="ghost"
                                    color="white"
                                    _hover={{ bg: "whiteAlpha.300" }}
                                    icon={
                                      sortColumn === "valor" ? (
                                        sortDirection === "asc" ? (
                                          <FaSortUp />
                                        ) : (
                                          <FaSortDown />
                                        )
                                      ) : (
                                        <FaSort />
                                      )
                                    }
                                    onClick={() => handleSort("valor")}
                                  />
                                  <Popover placement="bottom-start" isLazy>
                                    <PopoverTrigger>
                                      <IconButton
                                        aria-label="Filtrar valor"
                                        size="xs"
                                        variant="ghost"
                                        color="white"
                                        _hover={{ bg: "whiteAlpha.300" }}
                                        icon={<FaFilter />}
                                      />
                                    </PopoverTrigger>
                                    <PopoverContent
                                      minW="180px"
                                      maxH="280px"
                                      overflowY="auto"
                                      bg="white"
                                      color="gray.800"
                                      borderColor="gray.200"
                                    >
                                      <PopoverHeader
                                        fontWeight="semibold"
                                        color="gray.800"
                                      >
                                        Filtrar Valor
                                      </PopoverHeader>
                                      <PopoverBody color="gray.800">
                                        <CheckboxGroup
                                          value={columnFilters.valor}
                                          onChange={(vals) =>
                                            setColumnFilters((f) => ({
                                              ...f,
                                              valor: vals,
                                            }))
                                          }
                                        >
                                          <Flex direction="column" gap={1}>
                                            {distinctByColumn.valor.map((v) => (
                                              <Checkbox
                                                key={v}
                                                value={v}
                                                size="sm"
                                                colorScheme="blue"
                                                sx={{
                                                  "& .chakra-checkbox__label": {
                                                    color: "gray.800",
                                                  },
                                                }}
                                              >
                                                {v}
                                              </Checkbox>
                                            ))}
                                          </Flex>
                                        </CheckboxGroup>
                                        <Button
                                          size="xs"
                                          mt={2}
                                          onClick={() =>
                                            setColumnFilters((f) => ({
                                              ...f,
                                              valor: [],
                                            }))
                                          }
                                        >
                                          Limpar
                                        </Button>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Popover>
                                </HStack>
                              </HStack>
                            </Th>
                            <Th color="white">
                              <HStack spacing={1} justify="space-between">
                                <Text as="span">Prazo (dias)</Text>
                                <HStack spacing={0}>
                                  <IconButton
                                    aria-label="Ordenar prazo"
                                    size="xs"
                                    variant="ghost"
                                    color="white"
                                    _hover={{ bg: "whiteAlpha.300" }}
                                    icon={
                                      sortColumn === "prazo_pagamento" ? (
                                        sortDirection === "asc" ? (
                                          <FaSortUp />
                                        ) : (
                                          <FaSortDown />
                                        )
                                      ) : (
                                        <FaSort />
                                      )
                                    }
                                    onClick={() =>
                                      handleSort("prazo_pagamento")
                                    }
                                  />
                                  <Popover placement="bottom-start" isLazy>
                                    <PopoverTrigger>
                                      <IconButton
                                        aria-label="Filtrar prazo"
                                        size="xs"
                                        variant="ghost"
                                        color="white"
                                        _hover={{ bg: "whiteAlpha.300" }}
                                        icon={<FaFilter />}
                                      />
                                    </PopoverTrigger>
                                    <PopoverContent
                                      minW="140px"
                                      maxH="280px"
                                      overflowY="auto"
                                      bg="white"
                                      color="gray.800"
                                      borderColor="gray.200"
                                    >
                                      <PopoverHeader
                                        fontWeight="semibold"
                                        color="gray.800"
                                      >
                                        Filtrar Prazo
                                      </PopoverHeader>
                                      <PopoverBody color="gray.800">
                                        <CheckboxGroup
                                          value={columnFilters.prazo_pagamento}
                                          onChange={(vals) =>
                                            setColumnFilters((f) => ({
                                              ...f,
                                              prazo_pagamento: vals,
                                            }))
                                          }
                                        >
                                          <Flex direction="column" gap={1}>
                                            {distinctByColumn.prazo_pagamento.map(
                                              (v) => (
                                                <Checkbox
                                                  key={v}
                                                  value={v}
                                                  size="sm"
                                                  colorScheme="blue"
                                                  sx={{
                                                    "& .chakra-checkbox__label":
                                                      { color: "gray.800" },
                                                  }}
                                                >
                                                  {v}
                                                </Checkbox>
                                              )
                                            )}
                                          </Flex>
                                        </CheckboxGroup>
                                        <Button
                                          size="xs"
                                          mt={2}
                                          onClick={() =>
                                            setColumnFilters((f) => ({
                                              ...f,
                                              prazo_pagamento: [],
                                            }))
                                          }
                                        >
                                          Limpar
                                        </Button>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Popover>
                                </HStack>
                              </HStack>
                            </Th>
                            <Th color="white" width="100px">
                              Ações
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {paginatedList.map((row) => (
                            <Tr key={row.id}>
                              <Td fontWeight="medium">
                                {getCellDisplay("fornecedor", row)}
                              </Td>
                              {showProdutoColumn && (
                                <Td>{getCellDisplay("produto", row)}</Td>
                              )}
                              <Td>{getCellDisplay("data", row)}</Td>
                              <Td>{getCellDisplay("valor", row)}</Td>
                              <Td>{getCellDisplay("prazo_pagamento", row)}</Td>
                              <Td>
                                <Button
                                  size="xs"
                                  leftIcon={<FaEdit />}
                                  onClick={() => openEdit(row)}
                                >
                                  Editar
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    )}
                    {!loading && sortedList.length > 0 && (
                      <Flex
                        justify="space-between"
                        align="center"
                        px={4}
                        py={3}
                        borderTop="1px solid"
                        borderColor="gray.200"
                        flexWrap="wrap"
                        gap={3}
                      >
                        <Text fontSize="sm" color="gray.600">
                          Exibindo{" "}
                          {(tablePage - 1) * ITEMS_PER_PAGE + 1}–
                          {Math.min(
                            tablePage * ITEMS_PER_PAGE,
                            sortedList.length
                          )}{" "}
                          de {sortedList.length}
                        </Text>
                        {totalTablePages > 1 && (
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              variant="outline"
                              isDisabled={tablePage === 1}
                              onClick={() =>
                                setTablePage((p) => Math.max(1, p - 1))
                              }
                            >
                              Anterior
                            </Button>
                            <Text fontSize="sm" whiteSpace="nowrap">
                              Página {tablePage} de {totalTablePages}
                            </Text>
                            <Button
                              size="sm"
                              variant="outline"
                              isDisabled={tablePage >= totalTablePages}
                              onClick={() =>
                                setTablePage((p) =>
                                  Math.min(totalTablePages, p + 1)
                                )
                              }
                            >
                              Próxima
                            </Button>
                          </HStack>
                        )}
                      </Flex>
                    )}
                    {!loading && sortedList.length === 0 && (
                      <Center py={8} color="gray.500">
                        {listaPorProduto.length === 0
                          ? lista.length === 0
                            ? 'Nenhum preço registrado. Cadastre fornecedores e produtos e use "Registrar preço".'
                            : "Nenhum preço para este produto no período."
                          : "Nenhum registro corresponde aos filtros aplicados."}
                      </Center>
                    )}
                  </TableContainer>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Box>
      </Box>

      <ModalRegistrarPrecos
        isOpen={isOpen}
        onClose={onClose}
        fornecedores={fornecedores}
        produtos={produtos}
        onSuccess={loadPrecos}
      />
      <ModalEditarPreco
        isOpen={isEditOpen}
        onClose={closeEdit}
        row={editingRow}
        onSuccess={loadPrecos}
      />
      <Footer />
    </Flex>
  );
};

export default HistoricoPrecosPage;
